// Pixel-Sprites fuer "SiGeKo Run" (Wartungsseite Bauphase).
//
// Jede Zeile ist ein String, jedes Zeichen ein Pixel:
//   '.' leer, '#' Tinte (haengt von Tag/Nacht ab), o Helm, y Weste, f Haut,
//   b Hose, w weiss, r rot, s Stahl.
// Die Strings sind bewusst DOM-frei: die Spiel-Logik (runLogic.ts) liest nur
// Breite/Hoehe daraus und laeuft deshalb auch unter vitest ohne Canvas.

export type SpriteRows = readonly string[];

/** Farbpalette. '#' ist "ink" und wird beim Zeichnen durch die Tag-/Nachtfarbe ersetzt. */
export const PAL: Record<string, string> = {
    '#': 'ink',
    o: '#E8640C',
    y: '#F2C230',
    f: '#E9B98A',
    b: '#3A57B0',
    w: '#FFFFFF',
    r: '#C8321E',
    s: '#8A93A0',
};

/** Pixel-Massstab: ein Sprite-Pixel sind S Spiel-Pixel. */
export const S = 2;

export const HEAD: SpriteRows = [
    '......oooo......',
    '....oooooooo....',
    '...oooooooooo...',
    '..############..',
    '.....ffffff.....',
    '.....f#ff#f.....',
    '.....ffffff.....',
    '......ffff......',
];
export const BODY: SpriteRows = [
    '....yyyyyyyy....',
    '...fyy####yyf...',
    '...fyyyyyyyyf...',
    '...fyy####yyf...',
    '....yyyyyyyy....',
];
export const LEGS_STAND: SpriteRows = [
    '.....bbbbbb.....',
    '.....bb..bb.....',
    '.....bb..bb.....',
    '.....bb..bb.....',
    '.....##..##.....',
    '....###..###....',
];
export const LEGS_A: SpriteRows = [
    '.....bbbbbb.....',
    '....bb..bb......',
    '...bb....bb.....',
    '...##....bb.....',
    '..###.....##....',
    '..........###...',
];
export const LEGS_B: SpriteRows = [
    '.....bbbbbb.....',
    '......bb..bb....',
    '.....bb....bb...',
    '.....bb....##...',
    '....##.....###..',
    '...###..........',
];
export const DUCK: SpriteRows = [
    '..........oooo..',
    '........oooooooo',
    '......############',
    '.........ffffff.',
    '.........f#ff#f.',
    '..yyyyyyyyyyyy..',
    '.fy####yyyy####f',
    '..yyyyyyyyyyyy..',
    '..bbbb....bbbb..',
    '..##......##....',
    '.###.....###....',
];
export const FENCE: SpriteRows = [
    '#..............#',
    '################',
    '#.#.#.#.#.#.#.##',
    '##.#.#.#.#.#.#.#',
    '#.#.#.#.#.#.#.##',
    '##.#.#.#.#.#.#.#',
    '#.#.#.#.#.#.#.##',
    '##.#.#.#.#.#.#.#',
    '#.#.#.#.#.#.#.##',
    '################',
    '#..............#',
    '###..........###',
];
export const CONE: SpriteRows = [
    '....oo....',
    '....oo....',
    '...oooo...',
    '...wwww...',
    '..oooooo..',
    '..oooooo..',
    '.oooooooo.',
    '.wwwwwwww.',
    'oooooooooo',
    '##########',
];
export const BAGS: SpriteRows = [
    '......########......',
    '.....#........#.....',
    '.....#.##..##.#.....',
    '.....#........#.....',
    '......########......',
    '..########..########',
    '.#........##........#',
    '.#.##..##.##.##..##.#',
    '.#........##........#',
    '..########..########',
];
export const HOOK: SpriteRows = [
    '.........ss.........',
    '.........ss.........',
    '.........ss.........',
    '........####........',
    '.......##..##.......',
    '......##....##......',
    '......##............',
    '.......####.........',
    '....############....',
    '...#............#...',
    '...#.#########..#...',
    '...#............#...',
    '...##############...',
];
export const CRANE_BG: SpriteRows = [
    '#..................',
    '#####################',
    '#..#................#',
    '#..................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
    '.#.................',
];
export const CLOUD: SpriteRows = [
    '.....####.....',
    '...##....##...',
    '..#........#..',
    '.#..........#.',
    '#............#',
    '##############',
];

/** Haengt Sprite-Teile untereinander (Kopf + Rumpf + Beine). */
export function stack(...parts: SpriteRows[]): SpriteRows {
    let out: string[] = [];
    for (const p of parts) out = out.concat(p);
    return out;
}

export const RUN_A = stack(HEAD, BODY, LEGS_A);
export const RUN_B = stack(HEAD, BODY, LEGS_B);
export const JUMP = stack(HEAD, BODY, LEGS_STAND);

// Gerenderte Sprites werden je (Zeilen + Tintenfarbe) gecacht: das Zeichnen
// pro Frame ist dann ein drawImage statt hunderter fillRect-Aufrufe.
let cache: Record<string, HTMLCanvasElement> = {};

/** Leert den Sprite-Cache — beim Tag-/Nachtwechsel, wie im Prototyp. */
export function clearSpriteCache(): void {
    cache = {};
}

/**
 * Rendert die Pixelzeilen in ein Offscreen-Canvas (Massstab S). Die Breite
 * ist die laengste Zeile; kuerzere Zeilen enden einfach frueher.
 */
export function renderSprite(rows: SpriteRows, inkColor: string): HTMLCanvasElement {
    const key = rows.join('|') + inkColor;
    const hit = cache[key];
    if (hit) return hit;
    const w = Math.max(...rows.map(r => r.length));
    const h = rows.length;
    const c = document.createElement('canvas');
    c.width = w * S;
    c.height = h * S;
    const x = c.getContext('2d');
    if (x) {
        for (let j = 0; j < h; j++) {
            for (let i = 0; i < rows[j].length; i++) {
                const ch = rows[j][i];
                if (ch === '.') continue;
                const col = PAL[ch];
                if (!col) continue;
                x.fillStyle = col === 'ink' ? inkColor : col;
                x.fillRect(i * S, j * S, S, S);
            }
        }
    }
    cache[key] = c;
    return c;
}
